<template>
  <v-row class="user-bio-panel">
    <v-col cols="12">
      <v-card class="pt-10">
        <v-card-title
          v-touch="{
            left: () =>
              goToNext(positionData.positionIds, positionData.id, loading, {
                name: 'apps-project-position-view',
                param: 'id',
              }),
            right: () =>
              goToPrevious(positionData.positionIds, positionData.id, loading, {
                name: 'apps-project-position-view',
                param: 'id',
              }),
          }"
          class="justify-center flex-column"
        >
          <div class="d-flex justify-space-between align-center w-full mb-2">
            <v-btn
              icon
              color="primary"
              :disabled="!canGoToPrevious(positionData.positionIds, positionData.id) || loading"
              @click="
                goToPrevious(positionData.positionIds, positionData.id, loading, {
                  name: 'apps-project-position-view',
                  param: 'id',
                })
              "
              ><v-icon>{{ icons.mdiChevronLeft }}</v-icon></v-btn
            >
            <span class="text-center">{{ positionData.name }}</span>
            <v-btn
              icon
              color="primary"
              :disabled="!canGoToNext(positionData.positionIds, positionData.id) || loading"
              @click="
                goToNext(positionData.positionIds, positionData.id, loading, {
                  name: 'apps-project-position-view',
                  param: 'id',
                })
              "
            >
              <v-icon>{{ icons.mdiChevronRight }}</v-icon>
            </v-btn>
          </div>
        </v-card-title>

        <v-card-text>
          <h2 v-t="'details'" class="text-xl font-weight-semibold mb-2" />

          <v-divider></v-divider>

          <v-list>
            <v-list-item dense class="px-0 my-n2">
              <span class="font-weight-medium me-2">{{ $t('name') }}:</span>
              <span class="text--secondary">{{ positionData.name }}</span>
            </v-list-item>

            <v-list-item dense class="px-0 my-1">
              <span>
                <span class="font-weight-medium me-2">{{ $t('demand') }}:</span>
                <span class="text--secondary">{{ positionData.demand }}</span>
              </span>
            </v-list-item>

            <v-list-item dense class="px-0 my-n2">
              <span class="font-weight-medium me-2">{{ $t('workload') }}:</span>
              <span class="text--secondary"
                >{{ positionData.allAssigneesCount }}/{{ positionData.demand }} ({{ positionData.workload }}%)</span
              >
            </v-list-item>

            <v-list-item v-if="$can(ACTIONS.READ, PRIVATE.SHINE_FEATURES)" dense class="px-0 my-n2">
              <span class="font-weight-medium me-2">{{ $t('futureCount') }}:</span>
              <span class="text--secondary">{{ positionData.futureCount }} </span>
            </v-list-item>

            <v-list-item v-if="$can(ACTIONS.READ, PRIVATE.SHINE_FEATURES)" dense class="px-0 my-n2">
              <span class="font-weight-medium me-2">{{ $t('pastCount') }}:</span>
              <span class="text--secondary">{{ positionData.pastCount }} </span>
            </v-list-item>
          </v-list>
        </v-card-text>

        <v-card-actions>
          <v-row justify="center">
            <v-col cols="auto">
              <v-dialog v-model="isDeleteDialogOpen" max-width="650px" persistent>
                <template #activator="{ on, attrs }">
                  <v-btn color="error" v-bind="attrs" v-on="on" @click="isDeleteDialogOpen = true">
                    <v-icon class="me-2">{{ icons.mdiTrashCanOutline }}</v-icon>
                    <span v-t="'delete'" />
                  </v-btn>
                </template>
                <v-card class="pa-sm-10 pa-3">
                  <v-card-title v-t="'delete'" class="justify-center text-h5" />
                  <v-card-text>
                    <v-row>
                      <v-col cols="12" class="d-flex align-center justify-center">{{
                        $t('confirmationModalUndoneQuestion')
                      }}</v-col>
                    </v-row>
                    <v-row>
                      <v-col cols="12" class="d-flex justify-center mt-3">
                        <v-btn v-t="'discard'" outlined class="me-3" @click="isDeleteDialogOpen = false" />
                        <v-btn v-t="'delete'" color="error" @click="deletePositionHandler" />
                      </v-col>
                    </v-row>
                  </v-card-text>
                </v-card>
              </v-dialog>
            </v-col>
            <v-col cols="auto">
              <v-btn color="primary" @click="isBioDialogOpen = !isBioDialogOpen">
                <v-icon class="me-2">{{ icons.mdiPencilOutline }}</v-icon>
                <span v-t="'edit'" />
              </v-btn>
            </v-col>
          </v-row>
        </v-card-actions>
      </v-card>

      <position-bio-edit
        :is-bio-dialog-open.sync="isBioDialogOpen"
        :position-data.sync="positionData"
        :project-choices="projectChoices"
      ></position-bio-edit>
    </v-col>
  </v-row>
</template>

<script>
import { mdiTrashCanOutline, mdiPencilOutline, mdiOpenInNew, mdiChevronLeft, mdiChevronRight } from '@mdi/js'
import { ref } from '@vue/composition-api'
import { useActions, useState } from 'vuex-composition-helpers'
import { useRouter } from '@core/utils'
import PositionBioEdit from './PositionBioEdit.vue'
import { ACTIONS, PRIVATE } from '@/plugins/acl/const'
import navigationUtils from '@/utils/navigationUtils'

export default {
  components: {
    PositionBioEdit,
  },
  props: {
    projectChoices: {
      type: Array,
      required: true,
    },
    loading: {
      type: Boolean,
      required: true,
    },
  },
  setup() {
    const isBioDialogOpen = ref(false)
    const isDeleteDialogOpen = ref(false)
    const { router } = useRouter()

    const { deletePosition } = useActions('staff', ['deletePosition'])
    const { positionData } = useState('staff', ['positionData'])

    const { canGoToNext, goToNext, canGoToPrevious, goToPrevious } = navigationUtils()

    const deletePositionHandler = async () => {
      const { project } = positionData.value
      await deletePosition(positionData.value.id)
      await router.push({ name: 'apps-project-view', params: { id: project } })
    }

    const goToClientDetailsPage = (clientId) => {
      if (!clientId) return
      const route = router.resolve({ name: 'apps-client-view', params: { id: clientId } })
      window.open(route.href, '_blank')
    }

    return {
      deletePositionHandler,
      goToClientDetailsPage,
      canGoToNext,
      goToNext,
      canGoToPrevious,
      goToPrevious,

      positionData,
      isBioDialogOpen,
      isDeleteDialogOpen,
      icons: {
        mdiPencilOutline,
        mdiTrashCanOutline,
        mdiOpenInNew,
        mdiChevronLeft,
        mdiChevronRight,
      },
      ACTIONS,
      PRIVATE,
    }
  },
}
</script>
<style lang="scss">
.text--primary-link {
  color: var(--v-primary-base);
}
</style>
